// LeaveDialog — removing a person from the project is a LEAVE (ADR-081 §4): the
// dialog reads what it touches (the impact), asks place by place what becomes of the
// chains they hold (the handover body — a successor, or none: the next moves up or a
// gap remains), then ends the membership with the gaps it showed confirmed. The
// successors ride the handover tool first (through the chain's one choke point —
// the backing consent is the shared confirm-on-save flow, ADR-052 §5), the leave
// follows; a failure between the two leaves the person a member with their chains
// handed over — visible, never half done. The dialog is busy from the press to the
// last answer and never a frame in between (the two mutations' own flags plus the
// stretch between them); a refused handover is said here and hands the buttons back.
// Picks are work: every close path asks to discard them first (the staged guard).
// The sole-membership "remove & disable" flow rides the same dialog (`disable`).

import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DepartureImpact } from '@ems-app/schemas/users/responses';
import { Alert } from '../../components/ui/Alert.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { BackingConfirmDialog } from '../../components/patterns/BackingConfirmDialog.js';
import { useStagedGuard } from '../../components/patterns/useStagedGuard.js';
import { useDepartureImpact } from '../../hooks/useUsers.js';
import { useHandover, useRemoveUser } from '../../hooks/useUserMutations.js';
import { useResponsibilityChainSave } from '../../hooks/useResponsibilityChainSave.js';
import { apiErrorMessage } from '../../lib/apiError.js';
import { HandoverBody, chainKey, useBackingConsent, type SuccessorPick } from './HandoverBody.js';

export interface LeaveDialogProps {
  projectId: string;
  projectName: string;
  user: { user_id: string; name: string };
  open: boolean;
  /** The orphan flow: this is their last project — the account is disabled in the same stroke. */
  disable?: boolean;
  onClose: () => void;
  onRemoved: () => void;
}

export function LeaveDialog({ projectId, projectName, user, open, disable = false, onClose, onRemoved }: LeaveDialogProps) {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const impact = useDepartureImpact(projectId, user.user_id, open);
  const handover = useHandover(projectId);
  const remove = useRemoveUser(projectId);
  const [picks, setPicks] = useState<Record<string, SuccessorPick | null>>({});
  // The stretch the mutations' own flags do not cover: from the press until a mutation reports itself pending, and
  // between the handover's answer and the leave's send. Set on the press; cleared by every terminal outcome.
  const [between, setBetween] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The picks are saved once the handover answers: a leave refused after it leaves nothing to discard, and the next
  // press goes straight to the leave. A new pick makes them work again.
  const [handedOver, setHandedOver] = useState(false);
  const chainsId = useId();
  const busy = between || handover.isPending || remove.isPending;

  const chains = impact.data?.chains ?? [];
  const places = chains.flatMap((c) => {
    const p = picks[chainKey(c)];
    return p ? [{ responsibility_id: c.responsibility_id, level: c.level, scope_id: c.scope_id, successor_id: p.id }] : [];
  });

  const leave = async () => {
    try {
      await remove.mutateAsync({ userId: user.user_id, disable, confirmGaps: true });
      onRemoved();
    } catch (e) {
      setError(apiErrorMessage(e, tErrors));
    } finally {
      setBetween(false);
    }
  };
  // The successors first — the shared confirm-on-save flow (a 409 ASSIGNEE_NOT_BACKED opens the backing
  // confirm; confirming re-sends with grant_backing) — then the leave.
  const chainSave = useResponsibilityChainSave({
    submit: (grantBacking) => handover.mutateAsync({ userId: user.user_id, body: { places, grant_backing: grantBacking } }),
    onSaved: () => {
      setHandedOver(true);
      void leave();
    },
    // A refused handover (not held · an invalid successor · the network) is said below; the buttons come back.
    onError: () => setBetween(false),
    tErrors,
  });
  const confirm = () => {
    setError(null);
    setBetween(true);
    if (places.length > 0 && !handedOver) chainSave.save();
    else void leave();
  };
  const close = () => {
    setPicks({});
    setHandedOver(false);
    setError(null);
    chainSave.reset();
    onClose();
  };
  const guard = useStagedGuard({
    open,
    dirty: places.length > 0 && !handedOver,
    busy,
    onClose: close,
    discardTitle: t('departure.discardTitle'),
    discardBody: t('departure.discardBody'),
  });
  // The consent's confirm path keeps the dialog busy (the handover is re-sent, the leave follows); only a dismiss
  // without consent hands the buttons back. ConfirmDialog closes itself after a confirm, so a ref tells the two apart.
  const backingConfirmed = useRef(false);
  const consent = useBackingConsent(projectId, chains, picks, chainSave.backingPrompt);
  const gaps = chains.filter((c) => !picks[chainKey(c)]);
  const alone = gaps.filter((c) => c.alone).length;

  return (
    <>
      <Modal
        open={open}
        onClose={guard.requestClose}
        title={disable ? t('removeOrphan.title', { name: user.name }) : t('remove.title', { name: user.name, project: projectName })}
        maxWidth="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={guard.requestClose} disabled={busy}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="danger" onClick={confirm} disabled={busy || !impact.data} loading={busy}>
              {disable ? t('removeOrphan.confirm') : t('remove.confirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--color-text-secondary)]">{disable ? t('removeOrphan.body') : t('leave.body')}</p>
          {impact.isPending && !impact.data && (
            <div className="flex min-h-[4rem] items-center justify-center" role="status" aria-live="polite">
              <Spinner />
              <span className="sr-only">{tc('statuses.loading')}</span>
            </div>
          )}
          {impact.isError && !impact.data && (
            <Alert variant="danger">
              <span>{t('leave.impactError')}</span>{' '}
              <Button variant="link" onClick={() => void impact.refetch()}>
                {tc('actions.retry')}
              </Button>
            </Alert>
          )}
          {impact.data && <WhatEnds impact={impact.data} />}
          {chains.length > 0 && (
            <section className="flex flex-col gap-2" aria-labelledby={chainsId}>
              <h3 id={chainsId} className="font-medium">
                {t('departure.title', { count: chains.length })}
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">{t('departure.help')}</p>
              <HandoverBody
                projectId={projectId}
                userId={user.user_id}
                chains={chains}
                successors={picks}
                onChange={(k, pick) => {
                  setPicks((s) => ({ ...s, [k]: pick }));
                  setHandedOver(false);
                }}
                disabled={busy}
              />
              {gaps.length > 0 && <Alert variant={alone > 0 ? 'warning' : 'info'}>{alone > 0 ? t('departure.gapsAlone', { count: alone }) : t('departure.gapsCovered', { count: gaps.length })}</Alert>}
            </section>
          )}
          {(error ?? chainSave.error) && <Alert variant="danger">{error ?? chainSave.error}</Alert>}
        </div>
      </Modal>
      {guard.discardDialog}
      <BackingConfirmDialog
        open={chainSave.backingPrompt !== null}
        place={consent.place}
        names={consent.names}
        onConfirm={() => {
          backingConfirmed.current = true;
          chainSave.confirmBacking();
        }}
        onClose={() => {
          const confirmed = backingConfirmed.current;
          backingConfirmed.current = false;
          chainSave.dismissBacking();
          if (!confirmed) setBetween(false);
        }}
      />
    </>
  );
}

/** What the leave ends — said in the person's words, counted, under its own heading, nothing hidden (ADR-081 §4). */
function WhatEnds({ impact }: { impact: DepartureImpact }) {
  const { t } = useTranslation('users');
  const headingId = useId();
  const lines = [
    impact.scope_rows > 0 ? t('leave.ends.scope', { count: impact.scope_rows }) : null,
    impact.follows > 0 ? t('leave.ends.follows', { count: impact.follows }) : null,
    impact.saved_views > 0 ? t('leave.ends.views', { count: impact.saved_views }) : null,
    impact.teams.length > 0 ? t('leave.ends.teams', { count: impact.teams.length }) : null,
    impact.tokens > 0 ? t('leave.ends.tokens', { count: impact.tokens }) : null,
  ].filter((l): l is string => l !== null);
  if (lines.length === 0) return null;
  return (
    <section className="flex flex-col gap-1" aria-labelledby={headingId}>
      <h3 id={headingId} className="font-medium">
        {t('leave.ends.label')}
      </h3>
      <ul className="list-disc ps-5 text-sm" aria-labelledby={headingId}>
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </section>
  );
}
